import Bugsnag from '@bugsnag/js';
import { Button, DialogBody, DialogClose, DialogFooter } from '@civilgrid/ui/components';
import * as Progress from '@radix-ui/react-progress';
import ImageBlobReduce from 'image-blob-reduce';
import { nanoid } from 'nanoid';
import type React from 'react';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { addObjectFile as apiAddObjectFile } from '~/api/projects2';
import { isPermittingLocalMap } from '~/pages/projectDetails/pdfGeneration/permittingMapConstants';
import { useCivilDrawStore } from '../../civilDrawStoreContext';
import Import from './Import';

// not using webworker saves some CSP headache
const pica = new ImageBlobReduce.pica({
  features: ['js'],
});

const imageReducer = new ImageBlobReduce({ pica });

enum UploadState {
  SelectingFile = 'SelectingFile',
  Processing = 'Processing',
  FailedImport = 'FailedImport',
  Done = 'Done',
}

interface Props {
  closeDialog: () => void;
}

function SelectFile({ openFileSelector }: { openFileSelector: () => void }) {
  return (
    <Button onClick={() => openFileSelector()} data-testid="civildraw-image-upload-select-file">
      Select File
    </Button>
  );
}

function Cancel({ importRef }: { importRef: React.RefObject<HTMLInputElement | null> }) {
  return (
    <DialogClose asChild>
      <Button
        variant="secondary"
        data-testid="civildraw-image-upload-cancel"
        onClick={() => {
          if (importRef.current) {
            importRef.current.value = '';
          }
        }}
      >
        Cancel
      </Button>
    </DialogClose>
  );
}

const getDimsFromDataUrl = (dataURL: string): Promise<{ width: number; height: number }> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const originalWidth = img.width;
      const originalHeight = img.height;
      const aspectRatio = originalHeight / originalWidth;
      const width = Math.min(originalWidth, 300);
      const height = Math.round(width * aspectRatio);

      resolve({
        height,
        width,
      });
    };
    img.src = dataURL;
  });

export function ImageImport({ closeDialog }: Props) {
  const store = useCivilDrawStore();
  const map = useMap();
  const { projectId, currentMapId } = useSnapshot(store) as typeof store;
  const [currentUploadState, setCurrentUploadState] = useState<UploadState>(UploadState.SelectingFile);
  const importRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState(0);

  function openFileSelector() {
    if (importRef.current) {
      importRef.current.value = '';
      importRef.current.click();
    }
  }

  async function onFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) {
      const uploadedFile = e.target.files[0];
      const fileName = uploadedFile.name.replace(/\s/g, '_');
      const extension = uploadedFile.name.split('.').pop();
      if (!extension || !map.current) {
        return;
      }

      setCurrentUploadState(UploadState.Processing);

      try {
        const center = map.current.project(map.current.getCenter());
        const { width, height } = await getDimsFromDataUrl(window.URL.createObjectURL(uploadedFile));

        const optimizedBlob = await imageReducer.toBlob(uploadedFile, {
          max: 1024,
        });
        const optimizedFile = new File([optimizedBlob], fileName);

        // calculate map coordinates for image
        const screenCoords: [number, number][] = [
          [center.x - width / 2, center.y - height / 2],
          [center.x + width / 2, center.y - height / 2],
          [center.x + width / 2, center.y + height / 2],
          [center.x - width / 2, center.y + height / 2],
        ];

        const mapCoords = screenCoords.map((c) => map.current?.unproject(c).toArray()) as [number, number][];

        const imagePayload = {
          id: nanoid(),
          type: 'image' as const,
          title: fileName,
          sortOrder: store.generateSortOrderKey(),
          geometry: {
            type: 'Polygon' as const,
            coordinates: [[...mapCoords, mapCoords[0]]],
          },
          description: '',
          properties: {
            fillOpacity: 0.75,
            showTitle: true,
            color: 'red',
            minimized: false,
            showBorder: false,
          },
        };

        const permittingLocal = isPermittingLocalMap(currentMapId);

        const o = permittingLocal
          ? await store.newObject(
              {
                ...imagePayload,
                files: [
                  {
                    id: nanoid(),
                    path: URL.createObjectURL(optimizedFile),
                    name: fileName,
                    extension,
                    type: 'media',
                    uploadedAt: new Date().toISOString(),
                    uploadedBy: 'local',
                    notes: '',
                  },
                ],
              },
              false,
            )
          : await store.newObject(imagePayload, false);

        if (o) {
          if (!permittingLocal) {
            const data = new FormData();

            data.append('file', optimizedFile);
            data.append('name', fileName);
            data.append('type', 'media');
            data.append('extension', extension);
            setProgress(25);
            await apiAddObjectFile({
              projectId: projectId,
              mapId: currentMapId!,
              objectId: o.id,
              file: data,
            });
            setProgress(75);
            await store.refreshMap();
          }
          setProgress(100);
          setCurrentUploadState(UploadState.Done);
          store.setCurrentObjectId(o.id);
          store.shouldRedraw = true;
        } else {
          // TODO: Handle failure, delete object if it was created
        }
      } catch (e) {
        Bugsnag.notify(new Error(`CivilDraw file upload failed for image import`, { cause: e }));
        setCurrentUploadState(UploadState.FailedImport);
      }
    }
  }

  // open the file selector as soon as the dialog opens
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    store.resetCurrentObjectId();
    setTimeout(() => {
      openFileSelector();
    }, 0);
  }, []);

  let content: ReactNode;
  let footer: ReactNode = (
    <>
      <Cancel importRef={importRef} />
      <SelectFile openFileSelector={openFileSelector} />
    </>
  );

  if (currentUploadState === UploadState.SelectingFile) {
    content = <span>Select file to upload</span>;
  } else if (currentUploadState === UploadState.Processing) {
    content = (
      <div className="flex flex-col items-center">
        <div>{progress}%</div>
        <Progress.Root
          value={progress}
          className="relative h-6 w-72 overflow-hidden rounded-full bg-brand"
          style={{
            // Fix overflow skipping in safari per link below:
            // https://www.radix-ui.com/docs/primitives/components/progress
            transform: 'translateZ(0)',
          }}
        >
          <Progress.Indicator
            className="ease-[cubic-bezier(0.65, 0, 0.35, 1)] h-full w-full bg-primary transition-transform duration-[660ms]"
            style={{
              // translate not working with tailwind, maybe because @tailwind:base not implemented yet?
              transform: `translate(-${100 - progress}%)`,
            }}
          />
        </Progress.Root>
      </div>
    );
    footer = null;
  } else if (currentUploadState === UploadState.FailedImport) {
    content = <div>Image upload failed</div>;
  } else if (currentUploadState === UploadState.Done) {
    closeDialog();
    return null;
  }

  return (
    <>
      <DialogBody className="text-sm">{content}</DialogBody>
      <DialogFooter>{footer}</DialogFooter>
      <Import ref={importRef} onChange={onFileUpload} accept=".jpg, .jpeg, .png" />
    </>
  );
}
